import { useCallback, useEffect } from "react";
import { useAuth } from "@/lib/auth";
import { useNavigate } from "react-router";
import { useToast } from "@/components/ui/toast";
import { useTranslation } from "@/lib/i18n";
import { storeFederatedCredential } from "@/lib/fedcm";
import { resetCooldown } from "@/lib/gsiCooldown";
import { loadGsiScript } from "@/lib/loadGsiScript";

// ---------------------------------------------------------------------------
// Window type augmentation for Google Identity Services.
// Deprecated FedCM methods (isNotDisplayed, isSkippedMoment, getNotDisplayedReason,
// getSkippedReason) are intentionally omitted — use isDismissedMoment only.
// ---------------------------------------------------------------------------
declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            callback: (response: { credential: string; select_by?: string }) => void;
            auto_select?: boolean;
            cancel_on_tap_outside?: boolean;
            itp_support?: boolean;
            use_fedcm_for_prompt?: boolean;
            context?: "signin" | "signup" | "use";
            login_uri?: string;
            state_cookie_domain?: string;
          }) => void;
          prompt: (
            notification?: (notification: {
              isDismissedMoment: () => boolean;
              getDismissedReason: () => string;
              getMomentType: () => string;
            }) => void
          ) => void;
          renderButton: (
            parent: HTMLElement,
            options: {
              type?: "standard" | "icon";
              theme?: "outline" | "filled_blue" | "filled_black";
              size?: "large" | "medium" | "small";
              text?: "signin_with" | "signup_with" | "continue_with" | "signin";
              shape?: "rectangular" | "pill" | "circle" | "square";
              logo_alignment?: "left" | "center";
              width?: number | string;
              locale?: string;
            }
          ) => void;
          cancel: () => void;
          revoke: (hint: string, callback?: (response: { successful: boolean; error?: string }) => void) => void;
        };
      };
    };
  }
}

// ---------------------------------------------------------------------------
// Module-level singleton — prevents double-init races when both
// useGoogleOneTap and GoogleSignInButton mount simultaneously on the same page.
// ---------------------------------------------------------------------------
let _initialized = false;
let _consumerCount = 0;

export interface GoogleIdentityInitOptions {
  context?: "signin" | "signup" | "use";
  autoSelect?: boolean;
  cancelOnTapOutside?: boolean;
}

/**
 * Shared Google Identity Services initialization hook.
 *
 * Centralises the `google.accounts.id.initialize()` call so that
 * useGoogleOneTap and GoogleSignInButton share a single callback and config,
 * preventing double-init races and ensuring consistent FedCM opt-in.
 *
 * Returns `initialize()` — call it once Google's script has loaded.
 * Returns `true` if initialization succeeded (or was already done), `false` otherwise.
 */
export function useGoogleIdentityInit(options: GoogleIdentityInitOptions = {}) {
  const { context = "signin", autoSelect = true, cancelOnTapOutside = true } = options;

  const { loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { t } = useTranslation("auth");

  // Trigger lazy GSI script load and track consumer count so we reset the
  // module init flag when all consumers (One Tap + button) unmount.
  useEffect(() => {
    loadGsiScript();
    _consumerCount++;
    return () => {
      _consumerCount--;
      if (_consumerCount <= 0) {
        _initialized = false;
        _consumerCount = 0;
      }
    };
  }, []);

  const classifyGoogleError = useCallback(
    (error: unknown): { title: string; description: string } => {
      if (!navigator.onLine) {
        return { title: t("googleErrors.offline.title"), description: t("googleErrors.offline.description") };
      }

      const msg = (error instanceof Error ? error.message : String(error)).toLowerCase();
      const status = (error as { response?: { status?: number } })?.response?.status;

      if (msg.includes("popup") && (msg.includes("blocked") || msg.includes("failed to open"))) {
        return { title: t("googleErrors.popupBlocked.title"), description: t("googleErrors.popupBlocked.description") };
      }
      if (msg.includes("popup") && msg.includes("closed")) {
        return { title: t("googleErrors.popupClosed.title"), description: t("googleErrors.popupClosed.description") };
      }
      if (msg.includes("network") || msg.includes("fetch") || msg.includes("failed to fetch")) {
        return { title: t("googleErrors.network.title"), description: t("googleErrors.network.description") };
      }
      if (status === 409 || msg.includes("already exists") || msg.includes("email already")) {
        return { title: t("googleErrors.emailConflict.title"), description: t("googleErrors.emailConflict.description") };
      }
      if (status === 429 || msg.includes("too many")) {
        return { title: t("googleErrors.tooManyAttempts.title"), description: t("googleErrors.tooManyAttempts.description") };
      }
      if (status === 401 || msg.includes("expired") || msg.includes("invalid token")) {
        return { title: t("googleErrors.sessionExpired.title"), description: t("googleErrors.sessionExpired.description") };
      }

      return {
        title: t("googleErrors.generic.title"),
        description: error instanceof Error && error.message ? error.message : t("googleErrors.generic.description"),
      };
    },
    [t]
  );

  const handleCredentialResponse = useCallback(
    async (response: { credential: string; select_by?: string }) => {
      try {
        const result = await loginWithGoogle(response.credential);

        // Successful sign-in resets the One Tap dismissal cooldown
        resetCooldown();

        if (result.user?.email) {
          storeFederatedCredential(
            result.user.email,
            result.user.name || result.user.email,
            result.user.avatarUrl ?? undefined
          );
        }

        if (result.isNewUser) {
          navigate("/welcome");
        } else {
          if (localStorage.getItem("prezntz_trusted_device") !== "true") {
            sessionStorage.setItem("prezntz_show_trust_prompt", "true");
          }
          addToast({
            title: t("toast.welcomeBack"),
            description: t("toast.signedInGoogle"),
            variant: "success",
          });
          navigate("/dashboard");
        }
      } catch (error) {
        const { title, description } = classifyGoogleError(error);
        addToast({ title, description, variant: "destructive" });
      }
    },
    [loginWithGoogle, navigate, addToast, t, classifyGoogleError]
  );

  const initialize = useCallback((): boolean => {
    if (_initialized) return true;
    if (!window.google?.accounts?.id) return false;

    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
    if (!clientId) return false;

    _initialized = true;
    window.google.accounts.id.initialize({
      client_id: clientId,
      callback: handleCredentialResponse,
      auto_select: autoSelect,
      cancel_on_tap_outside: cancelOnTapOutside,
      itp_support: true,
      use_fedcm_for_prompt: true,
      context,
    });

    return true;
  }, [handleCredentialResponse, autoSelect, cancelOnTapOutside, context]);

  return { initialize };
}
